import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, XCircle, Play, ScanLine, KeyRound, LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";

const DocRow = ({ label, done }) => (
  <p className="flex items-center gap-2 text-sm font-ui">
    {done ? <CheckCircle2 size={15} className="text-emerald-600" /> : <XCircle size={15} className="text-amber-500" />}
    <span className={done ? "text-[#1A1A1A]" : "text-amber-700"}>{label} — {done ? "done" : "pending"}</span>
  </p>
);

export default function AdminScan() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [denied, setDenied] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [starting, setStarting] = useState(false);
  const [started, setStarted] = useState(null);
  const [readying, setReadying] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [latePrompt, setLatePrompt] = useState(false);
  const [lateHrs, setLateHrs] = useState("");
  const [refInput, setRefInput] = useState("");
  const [lookingUp, setLookingUp] = useState(false);

  useEffect(() => {
    if (!bookingId) return;
    api.get("/admin/bookings")
      .then((r) => {
        const b = r.data.find((x) => x.id === bookingId);
        if (b) setBooking(b); else setNotFound(true);
      })
      .catch(() => setDenied(true));
  }, [bookingId]);

  const lookupRef = async (e) => {
    e?.preventDefault();
    const code = refInput.trim().toUpperCase();
    if (!code) return;
    setLookingUp(true);
    try {
      const { data } = await api.get(`/admin/bookings/by-ref/${encodeURIComponent(code)}`);
      navigate(`/admin/scan/${data.id}`);
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setLookingUp(false);
    }
  };

  const begin = async () => {
    setStarting(true);
    try {
      const { data } = await api.post(`/admin/bookings/${bookingId}/begin-rental`);
      setStarted(data);
      setBooking((prev) => (prev ? { ...prev, rental_started_at: data.rental_started_at, vehicle_status: prev.vehicle_status || "preparing" } : prev));
      toast.success(data.already_started ? "This rental was already started" : "Rental started — customer notified by text & email to complete documents");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setStarting(false);
    }
  };

  const markReady = async () => {
    setReadying(true);
    try {
      const { data } = await api.post(`/admin/bookings/${bookingId}/vehicle-ready`);
      setBooking((prev) => (prev ? { ...prev, vehicle_status: "ready", vehicle_ready_at: data.vehicle_ready_at } : prev));
      toast.success(data.already_ready ? "Vehicle was already marked ready" : "Vehicle marked ready — customer notified by text & email");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setReadying(false);
    }
  };

  const staffCheckout = async () => {
    const accH = Number(booking?.late_accrual_hours) || 0;
    if (accH > 0 && !(Number(booking?.late_fee) > 0)) {
      setLateHrs(String(accH));
      setLatePrompt(true);
      return;
    }
    if (!window.confirm("Close this rental now? Use this for attended drop-offs where photos, fuel and deposit were handled in person.")) return;
    doCheckout();
  };

  const doCheckout = async (lateHours) => {
    setCheckingOut(true);
    try {
      const payload = { status: "completed", skip_return_checks: true };
      if (lateHours !== undefined) payload.late_hours = lateHours;
      const { data } = await api.patch(`/admin/bookings/${bookingId}`, payload);
      setBooking(data);
      setLatePrompt(false);
      toast.success(lateHours > 0 ? `Rental closed — late fee $${(lateHours * 25).toFixed(2)} applied, completion email & text sent` : "Rental closed — completion email & text sent to the customer");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setCheckingOut(false);
    }
  };

  if (!bookingId) {
    return (
      <div data-testid="staff-console-page" className="min-h-screen bg-[#F5EFE6] flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.06)] p-8">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#C71E2E]/10">
              <ScanLine size={20} className="text-[#C71E2E]" />
            </span>
            <h1 className="font-display text-2xl tracking-tight text-[#1A1A1A]">Staff Check-In / Rental Return</h1>
          </div>
          <p className="mt-4 text-sm font-ui text-[#4A4A4A] leading-relaxed">
            Scan the guest's pickup QR with your phone camera, type the reservation number, or scan the barcode on the invoice — barcode scanners type the code for you, then hit Find.
          </p>
          <form onSubmit={lookupRef} className="mt-6 space-y-3">
            <Input
              data-testid="staff-ref-input"
              autoFocus
              value={refInput}
              onChange={(e) => setRefInput(e.target.value.toUpperCase())}
              placeholder="Reservation no. e.g. ED-1A2B3C"
              className="h-12 text-base font-mono rounded-xl"
            />
            <Button data-testid="staff-ref-lookup-button" disabled={lookingUp || !refInput.trim()} className="w-full rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui h-12 disabled:opacity-40">
              {lookingUp ? "Finding…" : "Find Booking"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-scan-page" className="min-h-screen bg-[#F5EFE6] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.06)] p-8">
        <h1 className="font-display text-2xl tracking-tight text-[#1A1A1A]">Rental Check-In Scan</h1>
        {denied && (
          <div className="mt-4">
            <p data-testid="scan-denied" className="text-sm font-ui text-red-600">Staff login required to use the scanner.</p>
            <Link to="/admin-login"><Button data-testid="scan-login-link" className="mt-4 rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui">Go to Staff Login</Button></Link>
          </div>
        )}
        {notFound && !denied && <p data-testid="scan-not-found" className="mt-4 text-sm font-ui text-red-600">Booking not found — check the QR code and try again.</p>}
        {booking && (
          <div className="mt-5">
            <p data-testid="scan-booking-ref" className="font-ui font-semibold text-[#C71E2E]">{booking.booking_ref}</p>
            <p data-testid="scan-vehicle" className="mt-1 font-display text-xl text-[#1A1A1A]">{booking.vehicle_name}</p>
            <p className="mt-1 text-sm font-ui text-[#4A4A4A]">{booking.customer_name} · {booking.start_date} → {booking.end_date}</p>
            <div data-testid="scan-doc-status" className="mt-4 space-y-1.5 border-t border-[#E5DED5] pt-4">
              <DocRow label="Contract signed" done={!!booking.contract_signed_at} />
              <DocRow label="Driver's license uploaded" done={!!booking.license_uploaded} />
              <DocRow label="Payment confirmed" done={booking.status === "confirmed"} />
            </div>

            {booking.status === "completed" ? (
              <div data-testid="checkout-done-note" className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="font-ui font-semibold text-emerald-700 flex items-center gap-2"><LogOut size={15} /> Rental closed</p>
                <p className="mt-1 text-xs font-ui text-emerald-700">This rental has been checked out{booking.checked_out_manually ? " manually by staff" : ""}.</p>
              </div>
            ) : (
              <>
                {(booking.vehicle_status === "preparing" || booking.vehicle_status === "ready") && (
                  <div data-testid="scan-vehicle-status" className={`mt-4 rounded-xl border p-3.5 flex items-center gap-2.5 ${booking.vehicle_status === "ready" ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
                    {booking.vehicle_status === "ready" ? (
                      <>
                        <KeyRound size={15} className="text-emerald-600 shrink-0" />
                        <p className="text-xs font-ui font-semibold text-emerald-700">Vehicle marked READY — guest notified</p>
                      </>
                    ) : (
                      <>
                        <Loader2 size={15} className="text-amber-600 shrink-0 animate-spin" />
                        <p className="text-xs font-ui font-semibold text-amber-700">Vehicle being prepared — tap Mark Ready when done</p>
                      </>
                    )}
                  </div>
                )}

                {started ? (
                  <div data-testid="begin-rental-success" className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                    <p className="font-ui font-semibold text-emerald-700">{started.already_started ? "Rental was already active" : "Rental period started"}</p>
                    <p className="mt-1 text-xs font-ui text-emerald-700">The customer has been notified by text &amp; email to complete their documents.</p>
                  </div>
                ) : booking.rental_started_at ? (
                  <div className="mt-6 space-y-3">
                    <div data-testid="rental-active-note" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                      <p className="font-ui font-semibold text-emerald-700">Rental active</p>
                      <p className="mt-1 text-xs font-ui text-emerald-700">Started {new Date(booking.rental_started_at).toLocaleString()}</p>
                    </div>
                    <Link to={`/admin?tab=bookings&booking=${bookingId}`}>
                      <Button data-testid="open-return-checklist" className="w-full rounded-full bg-[#1A1A1A] text-white hover:bg-black font-ui">Open Return Checklist (drop-off)</Button>
                    </Link>
                  </div>
                ) : (
                  <Button
                    data-testid="begin-rental-button"
                    onClick={begin}
                    disabled={starting || booking.status !== "confirmed"}
                    className="mt-6 w-full rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui disabled:opacity-40"
                  >
                    <Play size={14} className="mr-2" />
                    {starting ? "Starting…" : booking.status === "confirmed" ? "Begin Rental Period" : "Booking not paid yet"}
                  </Button>
                )}

                {booking.status === "confirmed" && booking.vehicle_status !== "ready" && (
                  <Button
                    data-testid="mark-ready-button"
                    onClick={markReady}
                    disabled={readying}
                    variant="outline"
                    className="mt-3 w-full rounded-full border-emerald-500/50 bg-transparent text-emerald-700 hover:bg-emerald-600 hover:text-white font-ui disabled:opacity-40"
                  >
                    <KeyRound size={14} className="mr-2" />
                    {readying ? "Marking…" : "Mark Vehicle Ready (notify guest)"}
                  </Button>
                )}

                {booking.status === "confirmed" && !latePrompt && (
                  <Button
                    data-testid="staff-checkout-button"
                    onClick={staffCheckout}
                    disabled={checkingOut}
                    variant="outline"
                    className="mt-3 w-full rounded-full border-[#1A1A1A]/30 bg-transparent text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white font-ui disabled:opacity-40"
                  >
                    <LogOut size={14} className="mr-2" />
                    {checkingOut ? "Closing…" : "Rental Return / Close Rental"}
                  </Button>
                )}
                {latePrompt && (
                  <div data-testid="late-confirm-panel" className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-4">
                    <p className="font-ui font-semibold text-amber-800 text-sm">Guest is {booking.late_accrual_hours}h overdue</p>
                    <p className="mt-1 text-xs font-ui text-amber-700">Accrued late fee: ${(Number(booking.late_accrual_fee) || 0).toFixed(2)} @ $25/hr. Confirm the hours to bill, or waive.</p>
                    <div className="mt-3 flex items-center gap-2">
                      <Input data-testid="late-hours-input" type="number" min="0" step="0.5" value={lateHrs} onChange={(e) => setLateHrs(e.target.value)} className="h-9 w-24 text-sm rounded-lg bg-white" />
                      <span data-testid="late-fee-preview" className="text-xs font-ui text-amber-700">hr × $25 = ${((parseFloat(lateHrs) || 0) * 25).toFixed(2)}</span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <Button data-testid="late-apply-close-button" onClick={() => doCheckout(parseFloat(lateHrs) || 0)} disabled={checkingOut} className="rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui h-10 text-xs">
                        {checkingOut ? "Closing…" : "Apply fee & close"}
                      </Button>
                      <Button data-testid="late-waive-close-button" onClick={() => doCheckout(0)} disabled={checkingOut} variant="outline" className="rounded-full border-amber-400 bg-transparent text-amber-800 hover:bg-amber-100 font-ui h-10 text-xs">
                        Waive fee & close
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
